import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { doc, updateDoc } from "firebase/firestore";
import { db } from "../../lib/firebase";
import { useAuthContext } from "../../contexts/AuthContext";
import ThemeSelector, { ThemeLayout } from "../../components/ThemeSelector";
import { ArrowRight, Sparkles } from "lucide-react";

export default function ThemeSetup() {
  const [searchParams] = useSearchParams();
  const tier = searchParams.get("tier");
  const { user } = useAuthContext();
  const navigate = useNavigate();

  const [selectedTheme, setSelectedTheme] = useState<ThemeLayout>("classic");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!user || !tier) {
      navigate("/pricing");
      return;
    }

    if (tier !== "yearly") {
      navigate("/instructor/dashboard");
    }
  }, [user, tier, navigate]);

  const handleSaveTheme = async () => {
    if (!user) return;

    setSaving(true);
    setError("");

    try {
      await updateDoc(doc(db, "users", user.uid), {
        themeLayout: selectedTheme,
      });

      navigate("/instructor/dashboard");
    } catch (err: any) {
      console.error("Error saving theme:", err);
      setError("เกิดข้อผิดพลาดในการบันทึกธีม กรุณาลองใหม่อีกครั้ง");
    } finally {
      setSaving(false);
    }
  };

  const handleSkip = () => {
    navigate("/instructor/dashboard");
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-blue-50 py-12 px-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-blue-600 to-pink-600 rounded-3xl mb-6 shadow-2xl">
            <Sparkles className="w-10 h-10 text-white" />
          </div>
          <h1 className="text-4xl md:text-5xl font-black text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-pink-600 mb-4">
            ยินดีต้อนรับสู่ Yearly!
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            เลือกธีมเว็บไซต์ที่เหมาะกับแบรนด์ของคุณ คุณสามารถเปลี่ยนได้ทุกเมื่อในหน้าตั้งค่า
          </p>
        </div>

        {/* Theme Selector */}
        <div className="bg-white rounded-3xl shadow-2xl p-8 md:p-12 mb-8">
          <ThemeSelector
            selectedTheme={selectedTheme}
            onThemeSelect={setSelectedTheme}
            disabled={saving}
          />
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border-2 border-red-200 rounded-xl p-4 mb-8 text-center">
            <p className="text-red-600">{error}</p>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
          <button
            onClick={handleSkip}
            disabled={saving}
            className="px-8 py-4 text-gray-600 hover:text-gray-800 font-semibold transition-colors disabled:opacity-50"
          >
            ข้ามไปก่อน
          </button>
          <button
            onClick={handleSaveTheme}
            disabled={saving}
            className="px-12 py-4 bg-gradient-to-r from-blue-600 to-pink-600 text-white rounded-xl font-bold text-lg hover:shadow-2xl transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-3"
          >
            {saving ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                กำลังบันทึก...
              </>
            ) : (
              <>
                บันทึกและดำเนินการต่อ
                <ArrowRight className="w-5 h-5" />
              </>
            )}
          </button>
        </div>

        {/* Additional Info */}
        <div className="mt-12 text-center">
          <p className="text-sm text-gray-500">
            💡 คุณสามารถเปลี่ยนธีมได้ทุกเมื่อที่ <strong>ตั้งค่า → ธีมเว็บไซต์</strong>
          </p>
        </div>
      </div>
    </div>
  );
}
